import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { parseToMessagesFrontEndModel } from "./helpers/parseToMessagesFrontEndModel";
import useMessageQueries from "./hooks/useMessageQueries";
import {
  IConversationBackEndModel,
  IConversationFrontEndModel,
  IMessageFrontEndModel,
  IMessagesPageConfig,
} from "./types";
import { parseToConversationsFrontEndModel } from "./helpers/parseToConversationsFrontEndModel.tsx";
import { useQuery } from "react-query";
import { UserRole } from "../user/enums/UserRole";

const MessageContext = createContext<
  Partial<ReturnType<typeof useMessagesProvider>>
>({});

export const MessagesProvider: React.FC<
  { children: React.ReactNode } & IMessagesPageConfig
> = ({ children, ...params }) => {
  const provider = useMessagesProvider(params);

  return (
    <MessageContext.Provider value={provider}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessages = function () {
  return useContext(MessageContext);
};

const useMessagesProvider = function ({
  school,
  serviceProvider,
  student,
  userRole,
  recipientId,
  audioPlayer,
}: IMessagesPageConfig) {
  const { fetchConversations, fetchMessages, create } = useMessageQueries();
  const [selectedConversationInfo, setSelectedConversationInfo] = useState<{
    userRole: UserRole;
    id: string;
  }>();
  const [skipNextNotification, setSkipNextNotification] =
    useState<boolean>(false);
  const conversationsRef = useRef<IConversationFrontEndModel[]>([]);
  const seenMessagesIdsRef = useRef<string[]>([]);
  const authorType = school.id
    ? UserRole.School
    : serviceProvider.id
      ? UserRole.ServiceProvider
      : UserRole.Student;

  const getUpdatedConversations = async () => {
    const selectedSchoolId = userRole === UserRole.School ? recipientId : null;
    const selectedServiceProviderId =
      userRole === UserRole.ServiceProvider ? recipientId : null;
    const selectedStudentId =
      userRole === UserRole.Student ? recipientId : null;

    const conversationsFilter = {
      schoolId: school.id,
      serviceProviderId: serviceProvider.id,
      studentId: student.id,
      ...(selectedSchoolId && { selectedSchoolId }),
      ...(selectedServiceProviderId && { selectedServiceProviderId }),
      ...(selectedStudentId && { selectedStudentId }),
    };

    const receivedConversations = await fetchConversations(
      conversationsFilter,
    ).then(({ data }: { data: IConversationBackEndModel[] }) =>
      parseToConversationsFrontEndModel(data, authorType),
    );

    conversationsRef.current = receivedConversations.map((received) => {
      const current = conversations.find((item) => item.id === received.id);

      // new conversation
      if (!current) {
        // new received conversation wasn't seen and this is not first fetch
        if (!received.isSeen && conversationsRef.current.length) {
          audioPlayer?.current?.play();
        }
        return received;
      }

      if (
        current.created_at === received.created_at &&
        current.isSeen === received.isSeen
      ) {
        return current;
      }

      if (
        current.created_at !== received.created_at &&
        current.id !== selectedConversationInfo?.id
      ) {
        audioPlayer?.current?.play();
      }

      return {
        ...received,
        messages: current.messages,
        temp: current.temp,
      };
    });

    return conversationsRef.current;
  };

  const {
    data: conversationsData = [],
    refetch: refetchConversations,
    clear: clearConversation,
  } = useQuery<IConversationFrontEndModel[]>(
    ["fetchConversations", school.id || serviceProvider.id || student.id],
    () => getUpdatedConversations(),
    { refetchInterval: 10000 },
  );

  conversationsRef.current = conversationsData;
  const conversations = conversationsRef.current;
  const selectedConversation = conversations?.find(
    (conversation) => conversation.id === selectedConversationInfo?.id,
  );

  const changeSelectedConversationInfo = useCallback(
    (userRole: UserRole, id: string) => {
      setSelectedConversationInfo({ userRole, id });
      setSkipNextNotification(true);
    },
    [setSelectedConversationInfo],
  );

  const getMessages = async (conversation: IConversationFrontEndModel) => {
    let from: string = "";

    const schoolId =
      school.id ||
      (selectedConversationInfo?.userRole === UserRole.School
        ? selectedConversationInfo.id
        : null);
    const serviceProviderId =
      serviceProvider.id ||
      (selectedConversationInfo?.userRole === UserRole.ServiceProvider
        ? selectedConversationInfo.id
        : null);
    const studentId =
      student.id ||
      (selectedConversationInfo?.userRole === UserRole.Student
        ? selectedConversationInfo.id
        : null);

    const messagesFilter = {
      ...(schoolId && { schoolId }),
      ...(serviceProviderId && { serviceProviderId }),
      ...(studentId && { studentId }),
    };

    if (conversation?.messages.length > 0) {
      from = conversation.messages[0].created_at;
    }

    const seenMessagesIds = seenMessagesIdsRef.current;
    seenMessagesIdsRef.current = [];

    const { data } = await fetchMessages({
      ...messagesFilter,
      ...(from && {
        from,
      }),
      ...(seenMessagesIds?.length && {
        seenMessagesIds: seenMessagesIds.join(),
      }),
    });

    const parsedData = parseToMessagesFrontEndModel({
      messages: data,
      schoolPhoto: school.id ? school.image : conversation?.photo,
      serviceProviderPhoto: serviceProvider.id
        ? serviceProvider.image
        : conversation?.photo,
      studentPhoto: student.id ? student.image : conversation?.photo,
      userRole: authorType,
    });

    if (parsedData.length) {
      const current = conversationsRef.current.find(
        (item) => item.id === conversation.id,
      );

      current!.messages = [...parsedData, ...conversation.messages];
      refetchConversations();

      if (from && parsedData.some((m) => !m.isMine) && !skipNextNotification) {
        audioPlayer?.current?.play();
      }

      if (skipNextNotification) {
        setSkipNextNotification(false);
      }

      return current!.messages;
    }

    if (skipNextNotification) {
      setSkipNextNotification(false);
    }

    return conversation.messages;
  };

  const { refetch: refetchMessages, clear: clearMessages } = useQuery(
    ["fetchMessages", school.id || serviceProvider.id || student.id],
    () => getMessages(selectedConversation!),
    {
      refetchInterval: 5000,
      enabled: !!selectedConversation,
    },
  );

  useEffect(() => {
    if (selectedConversationInfo) {
      refetchMessages();
    }
  }, [selectedConversationInfo]);

  if (!selectedConversationInfo && conversations.length) {
    if (recipientId && userRole) {
      // select corresponding conversation if recipient id and user role is provided
      changeSelectedConversationInfo(userRole, recipientId);
    } else {
      // select first conversation if no recipient id is provided
      changeSelectedConversationInfo(
        conversations[0].userRole,
        conversations[0].id,
      );
    }
  }

  // on unmount clear conversations and messages caches
  useEffect(() => {
    return () => {
      clearConversation();
      clearMessages();
    };
  }, []);

  const sendMessage = useCallback(() => {
    if (!selectedConversation) {
      return;
    }

    const temp = selectedConversation.temp;

    const schoolId =
      school.id ||
      (selectedConversationInfo?.userRole === UserRole.School
        ? selectedConversationInfo.id
        : null);
    const serviceProviderId =
      serviceProvider.id ||
      (selectedConversationInfo?.userRole === UserRole.ServiceProvider
        ? selectedConversationInfo.id
        : null);
    const studentId =
      student.id ||
      (selectedConversationInfo?.userRole === UserRole.Student
        ? selectedConversationInfo.id
        : null);

    return create({
      ...(schoolId && { schoolId }),
      ...(serviceProviderId && { serviceProviderId }),
      ...(studentId && { studentId }),
      text: temp.text.trim() || null,
      attachments: temp.attachments.map((file) => ({
        fileId: file.id,
      })),
    }).then(() => {
      refetchMessages();
    });
  }, [selectedConversation]);

  const makeSeen = useCallback((message: IMessageFrontEndModel) => {
    message.isSeen = true;
    seenMessagesIdsRef.current.push(message.id);
  }, []);

  return {
    conversations,
    selectedConversation,
    changeSelectedConversationInfo,
    sendMessage,
    makeSeen,
  };
};
